import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { track } from '../analytics/events.js';
import { scoreToColor, scoreTextOn } from '../map/areaData.js';
import { buildSummary, getAreaFacts } from '../map/tooltipData.js';
import { scoreWord } from '../map/scoreWords.js';
import { scoreInk } from '../map/SearchScore.js';
import { DEFAULT_TREND_YEARS, fetchAreaSeries, priceChangeOverYears } from '../map/areaPriceTrend.js';
import AreaPriceChart from '../map/AreaPriceChart.js';
import { getListingKey, getListingShareParam } from '../map/listingDetailsApi.js';
import { getListingImage } from '../map/listingPlaceholder.js';
import { saveAreaPagePick } from '../map/savedSelection.js';
import { areaPagePath, placeQuery, readPlaceQuery, shareArea, sharePlace } from '../map/areaShare.js';
import { addressSalesLine, fetchAddressSales } from '../map/placeData.js';
import { areaMapShapes, findArea, loadAreaSources } from './areaSources.js';
import '../map/priceHistory.css';
import './areaPage.css';
import { apiFetch } from '../map/edgePayloads.js';

// The area page at /omrade/<RegSO code>/<name> (design S of the "Delningskort" canvas, 2026-10-31): where a
// shared area link lands. The area's name, its score and word, one sentence about it, its outline among the
// areas around it, the six numbers each with where it sits against nearby areas, the price trend and the
// homes for sale, with "Visa N bostäder" (listvy) and "Se på kartan" (kartvy), both with the area picked.
// A shared link unfurls with the area's own card (design A1, drawn by the backend's og_share.py).
// A shared place (?plats=<lat>,<lng>&namn=<address>, the map's "Gå till" pin, design B of the "Gå till-markör"
// canvas) adds a strip on top naming the address with its sales and "Se platsen på kartan", the site's pin on
// the outline map, and the address's sales as dots on the price chart.

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.xn--omrdeskollen-vcb.nu';
const HOMES_SHOWN = 3;
const SCALE_STEPS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6];
const NBSP = ' ';
const COPIED_MS = 1600;

const HOUSE_ICON = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 11l9-7 9 7v9a1 1 0 0 1-1 1h-5v-6h-6v6H4a1 1 0 0 1-1-1z" />
    </svg>
);
const SHARE_ICON = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 15V3" />
        <path d="M7.5 7.5L12 3l4.5 4.5" />
        <path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7" />
    </svg>
);

const formatScore = (score, swedish) => (swedish ? String(score).replace('.', ',') : String(score));
const formatKr = (value, swedish) => `${Math.round(value).toLocaleString(swedish ? 'sv-SE' : 'en-US').replace(/\s/g, NBSP)}${NBSP}kr`;
const formatDecimal = (value, swedish) => String(value).replace('.', swedish ? ',' : '.');

// Where a number sits against nearby areas, from its score; unemployment's score is turned round (a high
// score is low unemployment).
const compareText = (key, score, swedish) => {
    if (score == null) return '';
    const reversed = key === 'unemployment';
    const higher = swedish ? 'Högre än i närområdet' : 'Higher than nearby areas';
    const lower = swedish ? 'Lägre än i närområdet' : 'Lower than nearby areas';
    if (score >= 4) return reversed ? lower : higher;
    if (score <= 3) return reversed ? higher : lower;
    return swedish ? 'Som i närområdet' : 'Like nearby areas';
};

const trendSentence = (trend, swedish) => {
    if (!trend) return '';
    const up = trend.change >= 0;
    const amount = `${Math.abs(trend.change)}${NBSP}%`;
    const window = trend.full
        ? (swedish ? `sedan ${trend.startYear}` : `since ${trend.startYear}`)
        : (swedish ? `på ${DEFAULT_TREND_YEARS} år` : `in ${DEFAULT_TREND_YEARS} years`);
    const kind = trend.type === 'house' ? (swedish ? 'Villapriserna' : 'House prices') : (swedish ? 'Lägenhetspriserna' : 'Apartment prices');
    if (swedish) return `${kind} har gått ${up ? 'upp' : 'ner'} ${amount} ${window}.`;
    return `${kind} are ${up ? 'up' : 'down'} ${amount} ${window}.`;
};

function ScoreBadge({ score, swedish }) {
    const colour = score != null ? (scoreToColor[score] || '#8f9a97') : '#8f9a97';
    return (
        <span className="area-page-badge" style={{ '--ap-badge': colour, color: scoreTextOn(colour) }}>
            {HOUSE_ICON}
            {score != null && <span className="area-page-badge-score">{formatScore(score, swedish)}</span>}
        </span>
    );
}

const PIN_SVG = (
    <svg width="40" height="37" viewBox="-3 -3 745 689" aria-hidden="true">
        <path d="M372.6,80.2c126.6,0,229.1,102.6,229.1,229.1s-229.1,295-229.1,298.2S143.5,435.9,143.5,309.3S246.1,80.2,372.6,80.2z" fill="#1f7a62" stroke="#ffffff" strokeWidth="34" />
        <path d="M203.9,345c-3-12.6-4.4-25.4-4.4-38.4c0-95,80.9-167,167-167c67.8,0,130.9,44.6,157.9,112.6" fill="none" stroke="#ffffff" strokeWidth="30" />
        <path d="M531.2,333.9c-15.1,91-101.1,152.6-192.2,137.4c-66.5-11-119.8-60.8-135.3-126.4" fill="none" stroke="#ffffff" strokeWidth="30" />
        <path d="M532.8,344.3l-151.1-93.5c-8.4-5.2-19-5.2-27.3,0L201,344.5" fill="none" stroke="#ffffff" strokeWidth="30" />
        <path d="M385.1,407.3h-39.4c-5.3,0-10.2-2.1-14.1-5.8c-3.7-3.7-5.8-8.6-5.8-13.9v-39.2c0.1-11,9-19.7,19.9-19.7h39.4c11,0,19.8,8.7,19.9,19.7v39.2C404.9,398.5,395.9,407.3,385.1,407.3z" fill="none" stroke="#ffffff" strokeWidth="25" />
    </svg>
);

// The map panel's pixel size, to put the pin where the place is in the (letterboxed) outline.
const useBoxSize = (ref) => {
    const [size, setSize] = useState(null);
    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) return undefined;
        const update = () => setSize((prev) => (prev && prev.w === element.clientWidth && prev.h === element.clientHeight
            ? prev : { w: element.clientWidth, h: element.clientHeight }));
        update();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(update);
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);
    return size;
};

function OutlineMap({ shapes, colour, name, swedish, onPick, place }) {
    const ref = useRef(null);
    const size = useBoxSize(ref);
    let pin = null;
    if (shapes && place && size) {
        const [x, y] = shapes.project([place.lng, place.lat]);
        const scale = Math.min(size.w / shapes.box.w, size.h / shapes.box.h);
        const left = (size.w - shapes.box.w * scale) / 2 + (x - shapes.box.x) * scale;
        const top = (size.h - shapes.box.h * scale) / 2 + (y - shapes.box.y) * scale;
        if (left > 0 && left < size.w && top > 0 && top < size.h) pin = { left, top };
    }
    if (!shapes) return <div className="area-page-map" aria-hidden="true" />;
    return (
        <div className="area-page-map" ref={ref}>
            <svg viewBox={shapes.viewBox} preserveAspectRatio="xMidYMid meet" role="img"
                aria-label={swedish ? `${name} på kartan bland områdena runt omkring` : `${name} on the map among the areas around it`}>
                {shapes.others.map((other) => (
                    <a key={other.code} href={areaPagePath(other.code, other.name)} className="area-page-map-other"
                        onClick={(event) => { event.preventDefault(); onPick(other); }}>
                        <title>{other.kommun ? `${other.name}, ${other.kommun}` : other.name}</title>
                        <path d={other.d} fillRule="evenodd" vectorEffect="non-scaling-stroke" />
                    </a>
                ))}
                <path className="area-page-map-main" d={shapes.main} fillRule="evenodd" vectorEffect="non-scaling-stroke"
                    style={{ fill: colour, stroke: scoreInk(colour, 'light') }} />
            </svg>
            {pin && (
                <span className="area-page-map-pin" style={{ left: pin.left, top: pin.top }}>
                    {PIN_SVG}
                    <span className="area-page-map-pin-label">{place.name}</span>
                </span>
            )}
        </div>
    );
}

function StatTile({ fact, swedish }) {
    const position = fact.score != null ? ((((fact.score - 1) * 2) + 0.5) / 11) * 100 : null;
    const compare = compareText(fact.key, fact.score, swedish);
    return (
        <div className="area-page-stat">
            <span className="area-page-stat-value">{fact.value}</span>
            <span className="area-page-stat-caption">{fact.caption}</span>
            <span className={`area-page-stat-scale${position == null ? ' is-empty' : ''}`} aria-hidden="true">
                {SCALE_STEPS.map((step) => <i key={step} style={{ background: scoreToColor[step] }} />)}
                {position != null && <b style={{ left: `${position}%` }} />}
            </span>
            {compare && <span className="area-page-stat-compare">{compare}</span>}
        </div>
    );
}

function HomeCard({ listing, swedish, onOpen }) {
    const param = getListingShareParam(listing);
    const href = param ? `/?${param.name}=${param.value}` : '/';
    const price = Number(listing.price) > 0 ? formatKr(listing.price, swedish) : (swedish ? 'Pris saknas' : 'No price');
    const rooms = Number(listing.rooms) > 0 ? `${formatDecimal(listing.rooms, swedish)} ${swedish ? 'rum' : 'rooms'}` : '';
    const size = Number(listing.livingM2) > 0 ? `${formatDecimal(listing.livingM2, swedish)}${NBSP}m²` : '';
    const fee = Number(listing.fee) > 0 ? `${formatKr(listing.fee, swedish)}/${swedish ? 'mån' : 'mo'}` : '';
    return (
        <a className="area-page-home" href={href} onClick={(event) => { event.preventDefault(); onOpen(listing, href); }}>
            <img src={getListingImage(listing)} alt="" loading="lazy" />
            <span className="area-page-home-text">
                <b>{price}</b>
                <span>{listing.address}</span>
                <span className="area-page-home-facts">{[rooms, size, fee].filter(Boolean).join(' · ')}</span>
            </span>
        </a>
    );
}

export default function AreaPage({ languageState }) {
    const swedish = Boolean(languageState?.swedish);
    const { code: rawCode } = useParams();
    const code = String(rawCode || '').trim().toUpperCase();
    const navigate = useNavigate();
    const location = useLocation();
    const place = useMemo(() => readPlaceQuery(location.search), [location.search]);
    const [placeSales, setPlaceSales] = useState(null); // the address's sales, or null (none known)
    const scrollRef = useRef(null);

    const [sources, setSources] = useState(null);
    const [loadError, setLoadError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [homes, setHomes] = useState(null); // { items, total } | null while loading
    const [series, setSeries] = useState(null); // { type, data } | null
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoadError(false);
        loadAreaSources()
            .then((loaded) => { if (!cancelled) setSources(loaded); })
            .catch(() => { if (!cancelled) setLoadError(true); });
        return () => { cancelled = true; };
    }, [attempt]);

    const area = useMemo(() => (sources ? findArea(sources.topology, code) : null), [sources, code]);
    const shapes = useMemo(() => (sources && area ? areaMapShapes(sources.topology, code) : null), [sources, area, code]);
    // A shared place's sales, and its own home type's curve when it isn't the one shown.
    useEffect(() => {
        setPlaceSales(null);
        if (!place || !area?.kommun) return undefined;
        let cancelled = false;
        fetchAddressSales(place.name, area.kommun).then((sales) => { if (!cancelled) setPlaceSales(sales); });
        return () => { cancelled = true; };
    }, [place, area?.kommun]);
    const placeType = placeSales?.length ? placeSales[placeSales.length - 1].type : null;
    useEffect(() => {
        if (!placeType || !series || series.type === placeType) return undefined;
        let cancelled = false;
        fetchAreaSeries(code, placeType).then((data) => { if (!cancelled && data) setSeries({ type: placeType, data }); });
        return () => { cancelled = true; };
    }, [code, placeType, series]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
        setHomes(null);
        setSeries(null);
        let cancelled = false;
        apiFetch(`${API_BASE}/listings/active/regso/${encodeURIComponent(code)}?limit=${HOMES_SHOWN}&offset=0`)
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
                if (cancelled) return;
                setHomes({ items: Array.isArray(data?.items) ? data.items : [], total: Number(data?.total) || 0 });
            })
            .catch(() => { if (!cancelled) setHomes({ items: [], total: null }); });
        // The apartment curve, else the house curve (areas with few flats).
        fetchAreaSeries(code, 'apartment')
            .then((data) => (data ? { type: 'apartment', data } : fetchAreaSeries(code, 'house').then((house) => (house ? { type: 'house', data: house } : null))))
            .then((result) => { if (!cancelled) setSeries(result); });
        return () => { cancelled = true; };
    }, [code]);

    const score = useMemo(() => {
        const value = Number(sources?.stats?.[`combined_${code}`]);
        return Number.isFinite(value) ? value : null;
    }, [sources, code]);
    const details = useMemo(
        () => (sources && area ? getAreaFacts(code, sources.colorCache, sources.stats, swedish) : null),
        [sources, area, code, swedish],
    );
    const trend = useMemo(() => {
        if (!series?.data?.series) return null;
        const win = priceChangeOverYears(series.data.series, DEFAULT_TREND_YEARS);
        return win ? { ...win, type: series.type } : null;
    }, [series]);

    useEffect(() => {
        if (!area) return undefined;
        document.title = `${place ? `${place.name}, ` : ''}${area.name}${area.kommun ? `, ${area.kommun}` : ''} – hempejl`;
        track('area_page.view', { regso: code, score, place: Boolean(place) });
        return () => { document.title = 'hempejl'; };
        // One view per area shown.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [area?.code]);

    const goToView = useCallback((path, action, extra = {}) => {
        if (!area) return;
        saveAreaPagePick({ code, name: area.name, kommun: area.kommun });
        track('area_page.action', { action, regso: code, ...extra });
        navigate(path);
    }, [area, code, navigate]);

    const handleShare = useCallback(() => {
        if (!area) return;
        const run = place
            ? sharePlace({ code, areaName: area.name, place, source: 'page' })
            : shareArea({ code, name: area.name, source: 'page' });
        run.then((result) => {
            if (result !== 'copied') return;
            setCopied(true);
            setTimeout(() => setCopied(false), COPIED_MS);
        });
    }, [area, code, place]);

    const openNeighbour = useCallback((other) => {
        track('area_page.action', { action: 'neighbour', regso: code, to: other.code });
        navigate(areaPagePath(other.code, other.name));
    }, [code, navigate]);

    const openHome = useCallback((listing, href) => {
        goToView(href, 'listing', { listing_id: getListingKey(listing) });
    }, [goToView]);

    let body;
    if (loadError) {
        body = (
            <div className="area-page-message">
                <h1>{swedish ? 'Området gick inte att ladda' : 'The area could not be loaded'}</h1>
                <p>{swedish ? 'Försök igen om en stund.' : 'Please try again in a moment.'}</p>
                <button type="button" className="area-page-btn is-primary" onClick={() => setAttempt((n) => n + 1)}>
                    {swedish ? 'Försök igen' : 'Try again'}
                </button>
            </div>
        );
    } else if (!sources) {
        body = (
            <div className="area-page-hero is-loading" aria-busy="true" aria-label={swedish ? 'Laddar området' : 'Loading the area'}>
                <div className="area-page-hero-text">
                    <i className="area-page-skeleton" style={{ width: '40%' }} />
                    <i className="area-page-skeleton is-title" />
                    <i className="area-page-skeleton" style={{ width: '70%' }} />
                    <i className="area-page-skeleton" style={{ width: '85%' }} />
                </div>
                <div className="area-page-map" />
            </div>
        );
    } else if (!area) {
        body = (
            <div className="area-page-message">
                <h1>{swedish ? 'Vi hittar inte området' : "We can't find that area"}</h1>
                <p>{swedish ? 'Länken kan vara fel eller gammal. Sök efter området på kartan istället.' : 'The link may be wrong or old. Search for the area on the map instead.'}</p>
                <Link className="area-page-btn is-primary" to="/kartvy">{swedish ? 'Till kartan' : 'To the map'}</Link>
            </div>
        );
    } else {
        const word = score != null ? scoreWord(score, swedish) : '';
        const colour = score != null ? (scoreToColor[score] || '#8f9a97') : '#8f9a97';
        const summary = [buildSummary({ scores: details.scores, ownedShare: details.ownedShare }, swedish), trendSentence(trend, swedish)]
            .filter(Boolean).join(' ');
        const total = homes?.total;
        const tiles = [
            ...details.facts,
            { ...details.prices[0], caption: swedish ? 'lägenheter' : 'apartments' },
            { ...details.prices[1], caption: swedish ? 'villor' : 'houses' },
        ];
        const salesLine = addressSalesLine(placeSales, swedish);
        body = (
            <>
                {place && (
                    <div className="area-page-place">
                        <span className="area-page-place-text">
                            <b>{swedish ? `${place.name} ligger i ${area.name}` : `${place.name} is in ${area.name}`}</b>
                            {salesLine && <span>{salesLine}</span>}
                        </span>
                        <a href={`/kartvy?${placeQuery(place)}&omrade=${code}`} onClick={(event) => {
                            event.preventDefault();
                            track('area_page.action', { action: 'place_map', regso: code });
                            navigate(`/kartvy?${placeQuery(place)}&omrade=${code}`);
                        }}>
                            {swedish ? 'Se platsen på kartan' : 'See the place on the map'}
                            <span aria-hidden="true"> →</span>
                        </a>
                    </div>
                )}
                <section className="area-page-hero">
                    <div className="area-page-hero-text">
                        {area.kommun && <span className="area-page-kicker">{swedish ? `Område i ${area.kommun}` : `Area in ${area.kommun}`}</span>}
                        <h1>{area.name}</h1>
                        <div className="area-page-score">
                            <ScoreBadge score={score} swedish={swedish} />
                            <span className="area-page-score-text">
                                <b>{word ? `${word} ${swedish ? 'område' : 'area'}` : (swedish ? 'Inget betyg' : 'No score')}</b>
                                <span>
                                    {score != null
                                        ? (swedish ? `${formatScore(score, true)} av 6 jämfört med närområdet` : `${formatScore(score, false)} of 6 compared with nearby areas`)
                                        : (swedish ? 'För lite statistik för ett betyg' : 'Too little data for a score')}
                                </span>
                            </span>
                        </div>
                        <p className="area-page-summary">{summary}</p>
                        <div className="area-page-actions">
                            {total !== 0 && (
                                <a className="area-page-btn is-primary" href="/" onClick={(event) => { event.preventDefault(); goToView('/', 'listings'); }}>
                                    {total ? (swedish ? `Visa ${total} ${total === 1 ? 'bostad' : 'bostäder'}` : `Show ${total} ${total === 1 ? 'home' : 'homes'}`) : (swedish ? 'Visa bostäder' : 'Show homes')}
                                </a>
                            )}
                            <a className="area-page-btn is-secondary" href="/kartvy" onClick={(event) => { event.preventDefault(); goToView('/kartvy', 'map'); }}>
                                {swedish ? 'Se på kartan' : 'See on the map'}
                            </a>
                            <button type="button" className={`area-page-btn is-quiet${copied ? ' is-copied' : ''}`} onClick={handleShare}>
                                {SHARE_ICON}
                                {copied ? (swedish ? 'Länk kopierad!' : 'Link copied!') : (swedish ? 'Dela' : 'Share')}
                            </button>
                        </div>
                    </div>
                    <OutlineMap shapes={shapes} colour={colour} name={area.name} swedish={swedish} onPick={openNeighbour} place={place} />
                </section>

                <section className="area-page-section">
                    <div className="area-page-section-head">
                        <h2>{swedish ? `Livet i ${area.name}` : `Life in ${area.name}`}</h2>
                        <p>{swedish ? 'Strecket visar var området hamnar jämfört med närområdet.' : 'The mark shows where the area sits compared with nearby areas.'}</p>
                    </div>
                    <div className="area-page-stats">
                        {tiles.map((fact) => <StatTile key={fact.key} fact={fact} swedish={swedish} />)}
                    </div>
                </section>

                <section className="area-page-columns">
                    {series && (
                        <div className="area-page-prices">
                            <h2>{series.type === 'house' ? (swedish ? 'Villapriser i området' : 'House prices in the area') : (swedish ? 'Lägenhetspriser i området' : 'Apartment prices in the area')}</h2>
                            <AreaPriceChart series={series.data.series} estimate={null} homeSqm={null} swedish={swedish}
                                ownSales={placeSales?.filter((sale) => sale.type === series.type && sale.sqmPrice > 0) || null}
                                formatInt={(n) => Math.round(n).toLocaleString(swedish ? 'sv-SE' : 'en-US').replace(/\s/g, NBSP)} />
                        </div>
                    )}
                    <div className="area-page-homes">
                        <div className="area-page-homes-head">
                            <h2>{swedish ? `Till salu i ${area.name}` : `For sale in ${area.name}`}</h2>
                            {total > HOMES_SHOWN && (
                                <a href="/" onClick={(event) => { event.preventDefault(); goToView('/', 'listings_all'); }}>
                                    {swedish ? `Visa alla ${total}` : `Show all ${total}`}
                                    <span aria-hidden="true"> →</span>
                                </a>
                            )}
                        </div>
                        {homes == null ? (
                            <div className="area-page-homes-row">
                                {[0, 1, 2].map((n) => <i key={n} className="area-page-home is-loading" />)}
                            </div>
                        ) : homes.items.length ? (
                            <div className="area-page-homes-row">
                                {homes.items.map((listing) => (
                                    <HomeCard key={getListingKey(listing)} listing={listing} swedish={swedish} onOpen={openHome} />
                                ))}
                            </div>
                        ) : (
                            <p className="area-page-empty">{swedish ? 'Inga bostäder till salu här just nu.' : 'No homes for sale here right now.'}</p>
                        )}
                    </div>
                </section>

                <footer className="area-page-foot">
                    <span>
                        {swedish
                            ? `Betyget visar hur ${area.name} står sig mot områdena runt omkring, från 1 till 6. `
                            : `The score shows how ${area.name} compares with the areas around it, from 1 to 6. `}
                        <Link to="/faq">{swedish ? 'Vad betyder betyget?' : 'What does the score mean?'}</Link>
                    </span>
                </footer>
            </>
        );
    }

    return (
        <div className="area-page" ref={scrollRef}>
            <div className="area-page-inner">{body}</div>
        </div>
    );
}
